import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { withClientContext, client } from "~/apollo/client";
import {
  GetClientViewArenaDomainsDocument,
  GetClientViewDocument,
} from "~/graphql/supergraphHooks";
import {
  type GetClientViewArenaDomainsQuery,
  type GetClientViewQuery,
  type ClientViewSortInput,
  type ClientViewSortField,
  type SortDirection,
} from "~/graphql/supergraphTypes";
import { toGlobalId } from "~/utils/graphql-helpers";
import { CLIENTS_TABLE_PAGE_SIZE } from "./gridOptions";
import { ClientRowItem, ClientsData } from "./types";

const getSortVars = (sortModel: AgGrid.SortModelItem[] | null): ClientViewSortInput | null => {
  if (!sortModel || sortModel.length === 0) {
    return null;
  }

  const firstSort = sortModel[0];
  const fieldMap: Record<string, ClientViewSortField> = {
    arenaName: "ARENA_NAME",
    activeWorkflows: "ACTIVE_WORKFLOWS",
    dueIn5Days: "DUE_IN_5_DAYS",
    overdue: "OVERDUE",
  };

  const field = fieldMap[firstSort.colId];
  if (!field) {
    return null;
  }

  const direction: SortDirection = firstSort.sort === "asc" ? "ASC" : "DESC";

  return {
    field,
    direction,
  };
};

export const rowsFromQuery = (
  clients: ClientsData,
  dateRangeMetadata?: GetClientViewQuery["clientView"]["dateRangeMetadata"],
  activeStatuses?: string[] | null,
  secondLevelDomainMap?: Map<string, string | null>,
): ClientRowItem[] => {
  return clients.map((client) => ({
    arenaId: client.arenaId,
    arenaName: client.arenaName,
    activeWorkflows: client.activeWorkflows,
    workflowTypes: client.workflowTypes,
    dueIn5Days: client.dueIn5Days,
    overdue: client.overdue,
    dateRangeMetadata: dateRangeMetadata
      ? {
          dueIn5DaysStart: dateRangeMetadata.dueIn5DaysStart,
          dueIn5DaysEnd: dateRangeMetadata.dueIn5DaysEnd,
          overdueStart: null,
          overdueEnd: dateRangeMetadata.overdueBefore,
        }
      : null,
    activeStatuses: activeStatuses || null,
    secondLevelDomain: secondLevelDomainMap?.get(client.arenaId) ?? null,
  }));
};

export class ClientsGraphqlDataSource implements AgGrid.IServerSideDatasource {
  private client: ApolloClient<NormalizedCacheObject>;
  private pageSize: number = CLIENTS_TABLE_PAGE_SIZE;

  constructor(apolloClient: ApolloClient<NormalizedCacheObject> = client) {
    this.client = apolloClient;
  }

  public handleError(params: AgGrid.IServerSideGetRowsParams): void {
    params.fail();
  }

  public handleSuccess(
    params: AgGrid.IServerSideGetRowsParams,
    response: GetClientViewQuery,
    secondLevelDomainMap?: Map<string, string | null>,
  ): void {
    if (!response.clientView) {
      this.handleError(params);
      return;
    }

    const { rows, pageInfo, dateRangeMetadata, activeStatuses } = response.clientView;
    const rowData = rowsFromQuery(
      rows,
      dateRangeMetadata,
      activeStatuses,
      secondLevelDomainMap,
    );

    const rowCount = pageInfo.totalCount;

    params.success({
      rowData,
      rowCount,
    });

    if (rowData.length === 0 && rowCount === 0) {
      params.api?.showNoRowsOverlay();
    } else {
      params.api?.hideOverlay();
    }
  }

  private async fetchSecondLevelDomains(
    arenaIds: string[],
  ): Promise<Map<string, string | null>> {
    if (arenaIds.length === 0) return new Map();

    const globalToRawId = new Map(
      arenaIds.map((rawId) => [toGlobalId("ArenaObjectType", rawId), rawId]),
    );
    const globalIds = Array.from(globalToRawId.keys());

    try {
      const result = await this.client.query<GetClientViewArenaDomainsQuery>({
        query: GetClientViewArenaDomainsDocument,
        context: withClientContext("staff"),
        variables: { arenaIds: globalIds },
        fetchPolicy: "no-cache",
      });

      const map = new Map<string, string | null>();
      result.data?.arenas?.edges?.forEach((edge) => {
        const rawId = globalToRawId.get(edge.node.id);
        if (rawId !== undefined) {
          map.set(rawId, edge.node.secondLevelDomain ?? null);
        }
      });
      return map;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch arena secondLevelDomains", error);
      return new Map();
    }
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const startRow = params.request.startRow ?? 0;
    const endRow = params.request.endRow ?? startRow + this.pageSize;
    const limit = Math.max(endRow - startRow, this.pageSize);
    const page = Math.floor(startRow / limit) + 1;
    const sorts = getSortVars(params.request.sortModel);

    this.client
      .query<GetClientViewQuery>({
        query: GetClientViewDocument,
        variables: {
          sort: sorts,
          limit,
          page,
        },
        fetchPolicy: "no-cache",
      })
      .then(async (response) => {
        const arenaIds = (response.data?.clientView?.rows ?? [])
          .map((row) => row.arenaId)
          .filter((id): id is string => Boolean(id));

        const secondLevelDomainMap = await this.fetchSecondLevelDomains(arenaIds);
        this.handleSuccess(params, response.data, secondLevelDomainMap);
      })
      .catch(() => {
        this.handleError(params);
      });
  }
}
